
import React, { useState, useEffect, useRef } from 'react';
import { useLocalization } from '../hooks/useLocalization';
import type { Hotel, SearchQuery, TravelMateResponse, TravelMateSuggestionChip } from '../types';
import { getTravelMateAdvice } from '../services/aiService';
import { TravelVisualSearch } from './TravelVisualSearch';

interface TravelAssistantProps {
    isOpen: boolean;
    onClose: () => void;
    results: Hotel[];
    searchQuery: SearchQuery;
}

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content?: string;
    data?: TravelMateResponse;
}

export const TravelAssistant: React.FC<TravelAssistantProps> = ({ isOpen, onClose, results, searchQuery }) => {
    const { t, language } = useLocalization();
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Initial suggestion chips with icons
    const initialChips: TravelMateSuggestionChip[] = [
        { label: language === 'sv' ? "Hitta lyxvillor" : "Find luxury villas", action: "luxury", icon: "💎" },
        { label: language === 'sv' ? "Budgetvänliga alternativ" : "Budget friendly options", action: "budget", icon: "💰" },
        { label: language === 'sv' ? "Bästa för familjer" : "Best for families", action: "family", icon: "👨‍👩‍👧‍👦" },
        { label: language === 'sv' ? "Visa strandnära hotell" : "Show beach hotels", action: "beach", icon: "🏖️" }
    ];

    // Initialize with a welcome message and initial curated results
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            const welcomeMsg = language === 'sv'
                ? 'Hej! Jag är Petra AI, din personliga resestrateg. Jag har analyserat dagens priser och väderdata för att ge dig de bästa rekommendationerna. Här är mina toppval för din resa:'
                : 'Hello! I am Petra AI, your personal travel strategist. I have analyzed today\'s pricing and weather data to provide you with the best recommendations. Here are my top picks for your trip:';

            // Create initial interactive cards from existing results
            const initialCards = results.slice(0, 3).map((h, idx) => ({
                id: h.id,
                badge: h.rating >= 9 ? 'Elite Pick' : '95% Match',
                title: h.name,
                details_summary: h.board || 'All Inclusive',
                total_price: h.pricePerNight * (searchQuery.nights || 7),
                why_this_option: language === 'sv' ? `${h.name} erbjuder fantastiskt värde i ${h.location.city}.` : `${h.name} offers exceptional value in ${h.location.city}.`,
                image_url: h.imageUrl || h.thumbnailUrl,
                vibe: idx === 0 ? 'MODERN' : idx === 1 ? 'RELAXED' : 'BOUTIQUE',
                duration: language === 'sv' ? '7 Dagar · Sol & Bad' : '7 Days · Sun & Sea',
                best_season: 'Nov - Mar'
            }));

            setMessages([
                {
                    id: 'init-1',
                    role: 'assistant',
                    content: welcomeMsg,
                    data: {
                        assistant_reply: welcomeMsg,
                        status: 'success',
                        budget_analysis: { original_cost: 0, user_budget: 0, gap: 0 },
                        interactive_cards: initialCards,
                        suggestion_chips: initialChips
                    }
                }
            ]);
        }
    }, [isOpen, results, searchQuery, language]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleSendMessage = async (customInput?: string) => {
        const text = customInput || inputValue;
        if (!text.trim()) return;

        const userMsg: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: text
        };

        setMessages(prev => [...prev, userMsg]);
        if (!customInput) setInputValue('');
        setIsLoading(true);

        try {
            // Using the centralized aiService instead of a local instance
            const preferences = text.split(' ').filter(word => word.length > 3);
            const response = await getTravelMateAdvice(searchQuery, results, preferences, language === 'sv' ? 'sv' : 'en', searchQuery.adults * 500); // Dummy budget for analysis if not set

            if (response) {
                const botMsg: Message = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    data: response
                };
                setMessages(prev => [...prev, botMsg]);
            } else {
                throw new Error("No response from AI");
            }

        } catch (error: any) {
            console.error("AI Error", error);
            const errorMessage = language === 'sv'
                ? "Jag har lite svårt att svara just nu. Försök igen om en stund!"
                : "I'm having a bit of trouble right now. Please try again in a moment!";

            setMessages(prev => [...prev, {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: errorMessage
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCardClick = (id: string | number) => {
        const element = document.getElementById(`hotel-card-${id}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2');
            setTimeout(() => element.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2'), 3000);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white shadow-2xl z-50 flex flex-col animate-slideIn">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 p-6 flex items-center justify-between text-white shadow-xl relative overflow-hidden">
                <div className="absolute inset-0 bg-blue-500/5 mix-blend-overlay"></div>
                <div className="flex items-center gap-4 relative z-10">
                    <div className="relative">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-amber-500 to-orange-400 flex items-center justify-center shadow-xl border-2 border-white/20 animate-pulse">
                            <span className="text-2xl">🌍</span>
                        </div>
                        <span className="absolute bottom-0 right-0 block h-4 w-4 rounded-full ring-2 ring-gray-900 bg-green-400 shadow-sm"></span>
                    </div>
                    <div>
                        <h2 className="font-extrabold text-2xl leading-none tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">Petra AI</h2>
                        <span className="text-[10px] text-amber-400 uppercase tracking-[0.2em] font-black opacity-90">{t('travelmate_ai')} {t('beta')}</span>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-all group active:scale-95">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 group-hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Chat Area */}
            <div className="flex-grow overflow-y-auto p-4 space-y-8 bg-gray-50/30 scroll-smooth">
                {messages.map((msg, mIdx) => (
                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fadeInUp`} style={{ animationDelay: `${mIdx * 0.1}s` }}>
                        <div className={`max-w-[92%] ${msg.role === 'user' ? 'bg-gray-900 text-white rounded-2xl rounded-tr-none' : 'bg-white text-gray-800 border border-gray-100 rounded-2xl rounded-tl-none'} p-5 shadow-lg relative`}>
                            {/* Text Content */}
                            {msg.content && <p className="leading-relaxed whitespace-pre-wrap text-[15px]">{msg.content}</p>}

                            {/* Structured AI Response */}
                            {msg.data && (
                                <div className="space-y-6 mt-2">
                                    <p className="leading-relaxed font-semibold text-gray-800 text-[16px] border-l-4 border-amber-400 pl-3">
                                        {msg.data.assistant_reply}
                                    </p>

                                    {/* Route Insight Component */}
                                    {msg.data.route_insight && (
                                        <div className="bg-blue-600 text-white rounded-2xl p-6 shadow-xl relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 p-4 opacity-10 text-4xl transform group-hover:rotate-12 transition-transform select-none">📍</div>
                                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 text-blue-100">{language === 'sv' ? 'RUTTINSIKT' : 'ROUTE INSIGHT'}</h4>

                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest">{language === 'sv' ? 'STÄMNING' : 'VIBE'}</p>
                                                    <p className="text-sm font-black">{msg.data.route_insight.vibe}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest">{language === 'sv' ? 'TREND' : 'TREND'}</p>
                                                    <p className="text-sm font-black">{msg.data.route_insight.trend}</p>
                                                </div>
                                            </div>

                                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                                                <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest mb-1">{language === 'sv' ? 'LOKAL TIPS' : 'LOCAL TIP'}</p>
                                                <p className="text-xs italic leading-relaxed">"{msg.data.route_insight.local_tip}"</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Budget Analysis Component */}
                                    {msg.data.budget_analysis && msg.data.budget_analysis.user_budget > 0 && (
                                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-5 border border-gray-100 shadow-inner group overflow-hidden relative">
                                            <div className="absolute top-0 right-0 p-2 opacity-5 italic text-[10px] font-black">MARKET ANALYTICS</div>
                                            <div className="flex justify-between items-center mb-4">
                                                <span className="text-gray-400 text-[10px] font-black uppercase tracking-widest">{language === 'sv' ? 'BUDGETMATCHNING' : 'BUDGET MATCH'}</span>
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${msg.data.budget_analysis.gap > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                                                    {msg.data.budget_analysis.gap > 0 ? `+${msg.data.budget_analysis.gap} ${searchQuery.currency}` : 'On Target'}
                                                </span>
                                            </div>
                                            <div className="relative h-2 bg-gray-100 rounded-full mb-4">
                                                <div
                                                    className="absolute h-full bg-amber-400 rounded-full transition-all duration-1000 ease-out"
                                                    style={{ width: `${Math.min(100, (msg.data.budget_analysis.user_budget / msg.data.budget_analysis.original_cost) * 100)}%` }}
                                                ></div>
                                            </div>
                                            <div className="flex justify-between text-[11px] font-bold text-gray-500">
                                                <span>{language === 'sv' ? 'Mål' : 'Goal'}: {msg.data.budget_analysis.user_budget}</span>
                                                <span>{language === 'sv' ? 'Est.' : 'Est.'}: {msg.data.budget_analysis.original_cost}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Suggestion Chips - Updated Layout */}
                                    {msg.data.suggestion_chips && msg.data.suggestion_chips.length > 0 && (
                                        <div className="w-full overflow-x-auto no-scrollbar pb-2">
                                            <div className="flex gap-2 sm:flex-nowrap">
                                                {msg.data.suggestion_chips.map((chip, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => handleSendMessage(chip.label)}
                                                        className="flex-shrink-0 bg-gray-50 hover:bg-amber-50 text-gray-700 hover:text-amber-700 border border-gray-200 hover:border-amber-200 px-4 py-2 rounded-full text-xs font-bold transition-all transform hover:-translate-y-0.5 shadow-sm active:scale-95 flex items-center gap-2"
                                                    >
                                                        {/* Icon Only on Mobile if wanted, but user said 'as icons on mobile'. Text usually nicer. Let's do Icon+Text but formatted for single line. */}
                                                        {/* User asked: "Tailor your trip elements to fit on a single line on desktop and as icons on mobile." */}
                                                        {/* Implementation: Flex row for single line. For mobile 'as icons', we hide label? */}
                                                        <span className="text-base">{chip.icon || "✨"}</span>
                                                        <span className="hidden sm:inline">{chip.label}</span>
                                                        {/* Show label on mobile if space permits? User said 'as icons'. Let's trust that means Icon only on small screens. */}
                                                        {/* But wait, if it's icon only, how do they know what it is? Maybe tooltip? Or just the icon. */}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Visual Search Results (Replaces simple grid) */}
                                    {msg.data.interactive_cards && msg.data.interactive_cards.length > 0 && (
                                        <div className="pt-2">
                                            <TravelVisualSearch
                                                destinations={msg.data.interactive_cards.map(card => ({
                                                    id: String(card.id),
                                                    name: card.title,
                                                    country: '',
                                                    imageUrl: card.image_url || 'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?q=80&w=1000&auto=format&fit=crop',
                                                    matchPercentage: parseInt(card.badge) || 95,
                                                    bestSeason: card.best_season || 'Nov - Mar',
                                                    aiReason: card.why_this_option,
                                                    price: card.total_price,
                                                    currency: searchQuery.currency,
                                                    tags: [card.badge],
                                                    vibe: card.vibe,
                                                    duration: card.duration
                                                }))}
                                                onSelect={(dest) => handleCardClick(dest.id)}
                                                preferences={searchQuery.destinationName || "your dream trip"}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="flex justify-start animate-fadeIn">
                        <div className="bg-white p-6 rounded-2xl rounded-tl-none shadow-xl border border-gray-50">
                            <div className="flex flex-col space-y-3">
                                <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest animate-pulse">{language === 'sv' ? 'Petra AI tänker...' : 'Petra AI thinking...'}</span>
                                <div className="flex space-x-2">
                                    <div className="w-2.5 h-2.5 bg-amber-500 rounded-full animate-bounce transition-all duration-300"></div>
                                    <div className="w-2.5 h-2.5 bg-amber-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                    <div className="w-2.5 h-2.5 bg-amber-500 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-5 bg-white border-t border-gray-100 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.03)]">
                <form
                    onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
                    className="flex gap-3"
                >
                    <div className="relative flex-grow group">
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={language === 'sv' ? "T.ex: Strandnära med stor pool..." : "E.g: Beachfront with big pool..."}
                            className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 focus:bg-white transition-all text-sm placeholder-gray-400 font-bold outline-none shadow-sm"
                            disabled={isLoading}
                        />
                        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity">
                            <span className="text-[10px] font-black text-amber-500 active:scale-95">ENTER</span>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading || !inputValue.trim()}
                        className="bg-gray-900 hover:bg-amber-500 text-white hover:text-white p-4 rounded-2xl disabled:opacity-30 disabled:grayscale transition-all shadow-lg hover:shadow-amber-500/20 group transform hover:-translate-y-1 active:translate-y-0"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transform group-hover:rotate-12 transition-transform" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                        </svg>
                    </button>
                </form>
            </div>

            <style>{`
                @keyframes slideIn {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-slideIn { animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
            `}</style>
        </div>
    );
};
